import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Sparkles, Shield, DollarSign, AlertTriangle, Crown, Handshake,
  ChevronDown, ChevronUp, Target, Vote, MapPin, Menu, X, Map, Landmark,
} from 'lucide-react';
import axios from 'axios';
import useGameStore, { getOffice } from '../../game/gameStore';
import useAuthStore from '../../game/authStore';
import CandidatoAvatar from '../../components/CandidatoAvatar';
import TerritoryMap from '../../components/game/TerritoryMap';
import TerritoryGrid from '../../components/game/TerritoryGrid';
import ResourcePanel from '../../components/game/ResourcePanel';
import DecisionOverlay from './DecisionOverlay';
import ElectionOverlay from './ElectionOverlay';
import GameOverScreen from './GameOverScreen';
import TurnSummaryOverlay from './TurnSummaryOverlay';
import GabineteScreen from './GabineteScreen';
import { capitalize, STATE_CODE_MAP } from './constants';

function StatChip({ icon, label, value, suffix, color, danger }) {
  const pct = suffix === '%' ? Math.min(100, Math.max(0, value)) : Math.min(100, Math.max(0, (Math.max(0, value) / 500) * 100));
  const dangerHigh = danger && pct >= 60;
  const dangerCritical = danger && pct >= 80;
  return (
    <div className={`flex items-center gap-1 min-w-0 ${dangerCritical ? 'animate-pulse' : ''}`}>
      <span className={`shrink-0 ${color}`}>{icon}</span>
      <div className={`flex-1 h-1 rounded-full overflow-hidden min-w-[16px] sm:min-w-[24px] ${danger ? 'bg-red-900/30' : 'bg-white/[.06]'}`}>
        <div className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${pct}%`,
            background: danger
              ? (dangerCritical ? '#ef4444' : dangerHigh ? '#f87171' : '#fca5a5')
              : 'currentColor'
          }} />
      </div>
      <span className={`text-[10px] sm:text-xs font-bold shrink-0 ${dangerCritical ? 'text-red-400' : dangerHigh ? 'text-red-400' : color}`}>
        {value}{suffix}
      </span>
    </div>
  );
}

function SideSection({ title, icon, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: '#0c1628', border: '1px solid #152040' }}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-1.5 px-3 py-2 text-left hover:bg-white/[.02] transition-colors">
        {icon}
        <span className="text-[10px] font-semibold text-gray-300 uppercase tracking-wider flex-1">{title}</span>
        {open ? <ChevronUp size={12} className="text-gray-600" /> : <ChevronDown size={12} className="text-gray-600" />}
      </button>
      {open && <div className="px-3 pb-3 border-t border-white/[.04]">{children}</div>}
    </div>
  );
}

export default function GameBoard({
  playerAvatar, selectedCity, selectedState, cityDetail, selectedVereador, onRestart,
}) {
  const player = useGameStore(s => s.player);
  const turn = useGameStore(s => s.turn);
  const maxTurns = useGameStore(s => s.maxTurns);
  const currentOffice = useGameStore(s => s.currentOffice);
  const turnState = useGameStore(s => s.turnState);
  const objectives = useGameStore(s => s.objectives);
  const round = useGameStore(s => s.round);
  const phase = useGameStore(s => s.phase);
  const mapLevel = useGameStore(s => s.mapLevel);
  const territories = useGameStore(s => s.territories);
  const coalitionParties = useGameStore(s => s.coalitionParties);
  const playerParty = useGameStore(s => s.playerParty);
  const lastDecision = useGameStore(s => s.lastDecision);
  const cityData = useGameStore(s => s.cityData);
  const storeVereadores = useGameStore(s => s.vereadores);
  const playerVereadorId = useGameStore(s => s.playerVereadorId);
  const initDecisionDeck = useGameStore(s => s.initDecisionDeck);
  const initTerritories = useGameStore(s => s.initTerritories);
  const initBairrosTerritories = useGameStore(s => s.initBairrosTerritories);
  const initStateTerritories = useGameStore(s => s.initStateTerritories);
  const initCountryTerritories = useGameStore(s => s.initCountryTerritories);
  const endMapPhase = useGameStore(s => s.endMapPhase);
  const allocateResource = useGameStore(s => s.allocateResource);
  const generateObjectives = useGameStore(s => s.generateObjectives);
  const proposeCoalition = useGameStore(s => s.proposeCoalition);

  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [saveIndicator, setSaveIndicator] = useState(null);
  const [centralView, setCentralView] = useState('map'); // 'map' | 'gabinete'
  const lastSavedTurn = useRef(-1);

  const office = getOffice(currentOffice);
  const vereadores = cityDetail?.politicos?.eleitos?.filter(e => e.cargo === 'VEREADOR') || [];
  const prefeito = cityDetail?.politicos?.eleitos?.find(e => e.cargo === 'PREFEITO');
  const partidos = [...new Set(vereadores.map(v => v.partido).filter(Boolean))];

  // Auto-save
  useEffect(() => {
    const token = useAuthStore.getState().token;
    if (!token || phase !== 'playing') return;
    if (turn <= lastSavedTurn.current) return;
    lastSavedTurn.current = turn;

    const gameState = useGameStore.getState();
    const cityInfo = {
      cityName: cityData?.cityName,
      codigoIbge: cityData?.codigoIbge,
      stateSigla: cityData?.stateSigla,
    };

    const stateToSave = {
      player: gameState.player,
      turn: gameState.turn,
      maxTurns: gameState.maxTurns,
      phase: gameState.phase,
      turnPhase: gameState.turnPhase,
      currentOffice: gameState.currentOffice,
      round: gameState.round,
      careerPath: gameState.careerPath,
      playerParty: gameState.playerParty,
      mapLevel: gameState.mapLevel,
      territories: gameState.territories,
      vereadores: gameState.vereadores,
      playerVereadorId: gameState.playerVereadorId,
      coalitions: gameState.coalitions,
      coalitionParties: gameState.coalitionParties,
      resourcePool: gameState.resourcePool,
      partyInfluence: gameState.partyInfluence,
      objectives: gameState.objectives,
      cityData: gameState.cityData,
      turnState: gameState.turnState,
    };

    useAuthStore.getState().saveGame(stateToSave, playerAvatar, selectedVereador, cityInfo)
      .then(ok => {
        if (ok) {
          setSaveIndicator('saved');
          setTimeout(() => setSaveIndicator(null), 2000);
        }
      });
  }, [turn, phase, cityData, playerAvatar, selectedVereador]);

  const handlePlayAgain = useCallback(() => {
    const currentCityData = useGameStore.getState().cityData;
    const avatarBase = currentCityData?.avatarBase || {};
    const bonus = currentCityData?.vereadorBonus || {};
    useGameStore.getState().startGame(currentCityData, avatarBase, bonus);
    setMapLoaded(false);
    lastSavedTurn.current = -1;
  }, []);

  useEffect(() => {
    initDecisionDeck();
    generateObjectives();
  }, [initDecisionDeck, generateObjectives]);

  useEffect(() => {
    if (mapLoaded || Object.keys(territories).length > 0) return;

    const load = async () => {
      try {
        if (mapLevel === 'municipio' && cityData?.codigoIbge) {
          const tRes = await axios.get(`/geodata/territorios/${cityData.codigoIbge}`);
          if (tRes.data?.success && tRes.data.data) {
            const source = tRes.data.data.properties?.source;
            if (source === 'voronoi_bairros') {
              initBairrosTerritories(tRes.data.data);
            } else {
              initTerritories(tRes.data.data);
            }
            setMapLoaded(true);
          }
        } else if (mapLevel === 'estado' && cityData?.stateSigla) {
          const code = STATE_CODE_MAP[cityData.stateSigla.toUpperCase()];
          if (code) {
            const res = await axios.get(`/geodata/${code}`);
            if (res.data) initStateTerritories(res.data);
            setMapLoaded(true);
          }
        } else if (mapLevel === 'pais') {
          const res = await fetch('https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson');
          const data = await res.json();
          initCountryTerritories(data);
          setMapLoaded(true);
        }
      } catch (err) {
        console.error('GameBoard: map load error', err);
        setMapLoaded(true);
      }
    };
    load();
  }, [mapLevel, cityData, territories, mapLoaded, initTerritories, initBairrosTerritories, initStateTerritories, initCountryTerritories]);

  const handleTerritoryClick = (tId) => {
    if (turnState === 'map') setSelectedTerritory(tId);
  };

  const handleConfirmResolve = () => {
    setSelectedTerritory(null);
    endMapPhase();
  };

  const totalT = Object.keys(territories).length;
  const controlledT = Object.values(territories).filter(t => t.controlledBy === playerVereadorId).length;

  return (
    <div className="dark rounded-xl md:rounded-xl border border-[var(--border)] bg-[#0a0f1e] overflow-hidden" style={{ minHeight: '100vh' }}>
      {/* ── HUD Bar ── */}
      <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 border-b border-white/[.06]" style={{ background: '#060c1a' }}>
        {/* Office + City */}
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] sm:text-xs font-bold" style={{ color: '#d4a574' }}>{office.emoji}</span>
          <span className="text-[10px] font-semibold text-red-400 hidden sm:inline">
            {selectedCity?.name}
          </span>
        </div>

        {/* View toggle: Mapa / Gabinete */}
        <div className="flex rounded-lg overflow-hidden shrink-0" style={{ border: '1px solid #1e3050' }}>
          <button onClick={() => setCentralView('map')}
            className="flex items-center gap-1 px-1.5 sm:px-2.5 py-1 text-[9px] sm:text-[10px] font-bold transition-colors"
            style={{ background: centralView === 'map' ? '#14213d' : 'transparent', color: centralView === 'map' ? '#d4a574' : '#556688' }}>
            <Map size={10} /> <span className="hidden sm:inline">Mapa</span>
          </button>
          <button onClick={() => setCentralView('gabinete')}
            className="flex items-center gap-1 px-1.5 sm:px-2.5 py-1 text-[9px] sm:text-[10px] font-bold transition-colors"
            style={{ background: centralView === 'gabinete' ? '#14213d' : 'transparent', color: centralView === 'gabinete' ? '#d4a574' : '#556688' }}>
            <Landmark size={10} /> <span className="hidden sm:inline">Gabinete</span>
          </button>
        </div>

        {/* Stats */}
        <div className="flex-1 grid grid-cols-4 gap-1 sm:gap-2 mx-1 sm:mx-3 min-w-0">
          <StatChip icon={<Sparkles size={9} />} label="Car" value={player.popularity} suffix="%" color="text-green-400" />
          <StatChip icon={<Shield size={9} />} label="Infl" value={player.influence} suffix="%" color="text-purple-400" />
          <StatChip icon={<DollarSign size={9} />} label="R$" value={Math.max(0, player.money)} suffix="K" color="text-amber-400" />
          <StatChip icon={<AlertTriangle size={9} />} label="Ris" value={player.risk} suffix="%" color="text-red-400" danger />
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {saveIndicator === 'saved' && (
            <span className="text-[8px] sm:text-[9px] text-green-400 animate-pulse">💾</span>
          )}
          <span className="text-[9px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded" style={{ background: '#111828', color: '#8899bb', border: '1px solid #1e2d50' }}>
            <span className="hidden sm:inline">R{round} · </span>T{turn}/{maxTurns}
          </span>
          {/* Mobile sidebar toggle */}
          <button onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden p-1 rounded text-gray-500 hover:text-white">
            {sidebarOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
          <button onClick={onRestart} className="hidden sm:block text-[10px] font-bold px-2 py-1 rounded text-gray-500 hover:text-white border border-transparent hover:border-gray-600 transition-colors">
            Sair
          </button>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="flex flex-col md:flex-row" style={{ height: 'calc(100vh - 40px)' }}>
        {/* ── Central Area ── */}
        <div className="flex-1 relative min-w-0 min-h-0">
          {centralView === 'gabinete' ? (
            <GabineteScreen vereador={selectedVereador} playerAvatar={playerAvatar} cityName={selectedCity?.name} player={player} />
          ) : mapLevel === 'municipio' ? (
            <TerritoryGrid onTerritoryClick={handleTerritoryClick} selectedTerritory={selectedTerritory} onConfirmResolve={handleConfirmResolve} />
          ) : (
            <TerritoryMap onTerritoryClick={handleTerritoryClick} selectedTerritory={selectedTerritory} />
          )}

          {centralView === 'map' && turnState === 'map' && mapLevel !== 'municipio' && (
            <div className="absolute top-2 sm:top-3 left-1/2 -translate-x-1/2 z-20">
              <div className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-center" style={{ background: '#0b1424ee', border: '1px solid #fbbf2444' }}>
                <p className="text-[10px] sm:text-xs font-bold text-amber-400">ALOCAÇÃO</p>
                <p className="text-[9px] sm:text-[10px] text-gray-400 mt-0.5 hidden sm:block">Clique em territórios para investir</p>
              </div>
            </div>
          )}

          {centralView === 'map' && lastDecision && turnState === 'map' && mapLevel !== 'municipio' && (
            <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 z-20">
              <div className="flex gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg" style={{ background: '#0a1424ee', border: '1px solid #1a2848' }}>
                {lastDecision.pop !== 0 && <span className={`text-[10px] sm:text-[11px] font-bold ${lastDecision.pop > 0 ? 'text-green-400' : 'text-red-400'}`}>{lastDecision.pop > 0 ? '+' : ''}{lastDecision.pop}</span>}
                {lastDecision.influence !== 0 && <span className={`text-[10px] sm:text-[11px] font-bold ${lastDecision.influence > 0 ? 'text-purple-400' : 'text-red-400'}`}>{lastDecision.influence > 0 ? '+' : ''}{lastDecision.influence}</span>}
                {lastDecision.money !== 0 && <span className={`text-[10px] sm:text-[11px] font-bold ${lastDecision.money > 0 ? 'text-amber-400' : 'text-red-400'}`}>{lastDecision.money > 0 ? '+' : ''}{lastDecision.money}</span>}
              </div>
            </div>
          )}

          {centralView === 'map' && turnState === 'map' && mapLevel !== 'municipio' && selectedTerritory && (
            <div className="absolute bottom-14 left-2 sm:left-3 z-20 w-64 sm:w-72">
              <ResourcePanel selectedTerritory={selectedTerritory} onAllocate={allocateResource} onConfirmResolve={handleConfirmResolve} />
            </div>
          )}

          {centralView === 'map' && turnState === 'map' && !selectedTerritory && mapLevel !== 'municipio' && (
            <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 z-20">
              <button onClick={handleConfirmResolve}
                className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-[10px] sm:text-xs font-bold transition-all"
                style={{ background: '#14532d', color: '#4ade80', border: '1px solid #2a8040' }}>
                Confirmar
              </button>
            </div>
          )}
        </div>

        {/* ── Sidebar: hidden on mobile, slide-in drawer ── */}
        {/* Desktop sidebar */}
        <div className="hidden md:block w-64 lg:w-72 border-l border-white/[.04] overflow-y-auto"
          style={{ background: '#080e1a', scrollbarWidth: 'thin' }}>
          <SidebarContent
            playerAvatar={playerAvatar}
            selectedVereador={selectedVereador}
            objectives={objectives}
            totalT={totalT}
            controlledT={controlledT}
            territories={territories}
            storeVereadores={storeVereadores}
            playerVereadorId={playerVereadorId}
            prefeito={prefeito}
            coalitionParties={coalitionParties}
            partidos={partidos}
            playerParty={playerParty}
            turnState={turnState}
            proposeCoalition={proposeCoalition}
            onRestart={onRestart}
          />
        </div>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-72 bg-[#080e1a] border-l border-white/[.08] overflow-y-auto animate-[slideInRight_0.2s_ease-out]"
              style={{ scrollbarWidth: 'thin' }}>
              <div className="flex items-center justify-between p-3 border-b border-white/[.06]">
                <span className="text-xs font-bold text-gray-400">Menu</span>
                <button onClick={() => setSidebarOpen(false)} className="p-1 rounded hover:bg-white/10">
                  <X size={16} className="text-gray-500" />
                </button>
              </div>
              <SidebarContent
                playerAvatar={playerAvatar}
                selectedVereador={selectedVereador}
                objectives={objectives}
                totalT={totalT}
                controlledT={controlledT}
                territories={territories}
                storeVereadores={storeVereadores}
                playerVereadorId={playerVereadorId}
                prefeito={prefeito}
                coalitionParties={coalitionParties}
                partidos={partidos}
                playerParty={playerParty}
                turnState={turnState}
                proposeCoalition={proposeCoalition}
                onRestart={onRestart}
                isMobile
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Overlays ── */}
      {turnState === 'decision' && phase === 'playing' && <DecisionOverlay />}
      {turnState === 'turnSummary' && <TurnSummaryOverlay />}
      {turnState === 'mandate_end' && <ElectionOverlay />}
      {turnState === 'election' && <ElectionOverlay />}
      {(turnState === 'gameover' || phase === 'gameover') && (
        <GameOverScreen onPlayAgain={handlePlayAgain} onRestart={onRestart} />
      )}
    </div>
  );
}

function SidebarContent({
  playerAvatar, selectedVereador, objectives, totalT, controlledT,
  territories, storeVereadores, playerVereadorId, prefeito,
  coalitionParties, partidos, playerParty, turnState, proposeCoalition,
  onRestart, isMobile,
}) {
  return (
    <div className="p-2 space-y-2">
      {/* Player card */}
      <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: '#0c1628', border: '1px solid #152040' }}>
        {playerAvatar && (
          <div className="w-8 h-8 shrink-0 rounded-md overflow-hidden" dangerouslySetInnerHTML={{ __html: playerAvatar.svg }} />
        )}
        <div className="min-w-0">
          <p className="text-[10px] font-bold text-white truncate">{playerAvatar?.alias}</p>
          {selectedVereador && (
            <p className="text-[9px] text-gray-500 truncate">Assessor de {capitalize(selectedVereador.nome)}</p>
          )}
        </div>
      </div>

      <SideSection title="Objetivos" icon={<Target size={11} className="text-amber-400" />} defaultOpen>
        <div className="space-y-2 mt-2">
          {objectives.map(obj => (
            <div key={obj.id} className="flex items-start gap-2">
              <span className={`text-[10px] mt-0.5 ${obj.completed ? 'text-green-400' : 'text-gray-600'}`}>
                {obj.completed ? '✅' : '⬜'}
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-[10px] ${obj.completed ? 'text-green-400 line-through' : 'text-gray-400'}`}>{obj.text}</p>
                {!obj.completed && obj.target > 0 && (
                  <div className="h-0.5 rounded-full mt-0.5" style={{ background: '#141e30' }}>
                    <div className="h-full rounded-full bg-amber-500" style={{ width: `${Math.min(100, (obj.current / obj.target) * 100)}%` }} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </SideSection>

      {totalT > 0 && (
        <SideSection title={`Territórios (${controlledT}/${totalT})`} icon={<MapPin size={11} className="text-green-400" />} defaultOpen>
          <div className="mt-2 space-y-1">
            <div className="h-2 rounded-full overflow-hidden" style={{ background: '#141e30' }}>
              <div className="h-full rounded-full bg-green-400 transition-all" style={{ width: `${totalT > 0 ? (controlledT / totalT) * 100 : 0}%` }} />
            </div>
            <div className="flex gap-2 text-[9px]">
              <span className="text-green-400">{controlledT} seu</span>
              <span className="text-red-400">{Object.values(territories).filter(t => t.controlledBy && t.controlledBy !== playerVereadorId).length} rival</span>
              <span className="text-gray-500">{Object.values(territories).filter(t => !t.controlledBy).length} neutro</span>
            </div>
            {storeVereadores.length > 0 && (() => {
              const counts = {};
              for (const t of Object.values(territories)) {
                if (t.controlledBy) counts[t.controlledBy] = (counts[t.controlledBy] || 0) + 1;
              }
              const ranked = Object.entries(counts)
                .map(([id, c]) => ({ ver: storeVereadores.find(v => v.id === id), count: c }))
                .filter(r => r.ver)
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);
              return (
                <div className="pt-1.5 space-y-0.5">
                  {ranked.map(r => (
                    <div key={r.ver.id} className="flex items-center gap-1.5 text-[9px]">
                      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: r.ver.id === playerVereadorId ? '#4ade80' : '#ef4444' }} />
                      <span className={`truncate flex-1 ${r.ver.id === playerVereadorId ? 'text-green-400 font-bold' : 'text-gray-400'}`}>
                        {capitalize(r.ver.nome).split(' ').slice(0, 2).join(' ')}
                      </span>
                      <span className="text-gray-600 shrink-0">{r.ver.partido}</span>
                      <span className="text-gray-500 font-mono shrink-0">{r.count}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </SideSection>
      )}

      {storeVereadores.length > 0 && (
        <SideSection title={`Câmara (${storeVereadores.length})`} icon={<Vote size={11} className="text-purple-400" />}>
          <div className="mt-2 space-y-1 max-h-40 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {storeVereadores.slice(0, 20).map((v) => {
              const isPlayerBoss = v.id === playerVereadorId;
              return (
                <div key={v.id} className={`flex items-center gap-1.5 ${isPlayerBoss ? 'bg-green-500/5 -mx-1 px-1 rounded' : ''}`}>
                  <CandidatoAvatar candidato={v} foto={v.foto} nome={v.nome} size={16} className="rounded-full" />
                  <span className={`text-[9px] truncate flex-1 ${isPlayerBoss ? 'text-green-400 font-bold' : 'text-gray-400'}`}>
                    {capitalize(v.nome)}
                  </span>
                  <span className={`text-[8px] shrink-0 ${isPlayerBoss ? 'text-green-400 font-bold' : coalitionParties.includes(v.partido) ? 'text-blue-400' : 'text-gray-600'}`}>
                    {v.partido}
                  </span>
                </div>
              );
            })}
          </div>
        </SideSection>
      )}

      {prefeito && (
        <SideSection title="Prefeito(a)" icon={<Crown size={11} className="text-amber-400" />}>
          <div className="flex items-center gap-2 mt-2">
            <CandidatoAvatar candidato={prefeito} foto={prefeito.foto} nome={prefeito.nome} size={28} className="rounded-md" />
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-white truncate">{capitalize(prefeito.nome)}</p>
              <p className="text-[9px] text-gray-500">{prefeito.partido}</p>
            </div>
          </div>
        </SideSection>
      )}

      <SideSection title="Coligações" icon={<Handshake size={11} className="text-blue-400" />}>
        <div className="mt-2 space-y-1.5">
          {coalitionParties.length === 0 ? (
            <p className="text-[10px] text-gray-600">Nenhuma coligação formada</p>
          ) : coalitionParties.map(p => (
            <div key={p} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-400" />
              <span className="text-[10px] text-blue-300">{p}</span>
            </div>
          ))}

          {partidos.filter(p => p !== playerParty && !coalitionParties.includes(p)).length > 0 && turnState === 'map' && (
            <div className="pt-2 border-t border-white/[.04]">
              <p className="text-[9px] text-gray-600 mb-1">Propor coligação:</p>
              <div className="flex flex-wrap gap-1">
                {partidos.filter(p => p !== playerParty && !coalitionParties.includes(p)).slice(0, 6).map(p => (
                  <button key={p} onClick={() => proposeCoalition(p)}
                    className="text-[8px] px-1.5 py-0.5 rounded text-gray-400 hover:text-white transition-colors"
                    style={{ background: '#141e30', border: '1px solid #1e3050' }}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </SideSection>

      {/* Mobile-only: Sair button */}
      {isMobile && (
        <button onClick={onRestart}
          className="w-full py-2 rounded-lg text-xs font-bold text-gray-500 hover:text-white border border-white/10 hover:border-white/20 transition-colors mt-2">
          Sair do Jogo
        </button>
      )}
    </div>
  );
}
